import { Button, Colors, Dialog, Icon, Intent, Spinner, Tag } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import {
  closestCorners,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  horizontalListSortingStrategy,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import CX from 'classnames'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import ImageHelpers from './Helpers'
import config from '../../config'
import { UprightStyle } from '../../types'
import { SortableImageListProps } from './sortableImageListTypes'
import { getSortableImageId } from './sortableImageIds'

const style: UprightStyle = {
  zoomBtn: { borderRadius: 0, position: 'absolute', bottom: 7, left: 8, color: 'white', zIndex: 1, cursor: 'pointer' },
  zoomDialog: { paddingBottom: 0, position: 'relative', overflow: 'hidden', width: '700px' },
  zoomExitBtn: {
    position: 'absolute',
    zIndex: 1,
    top: 17,
    right: 45,
    display: 'block'
  },
  imgContainer: {
    padding: '5px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center'
  },
  thumbnailGalleryItem: { maxHeight: '100px', justifySelf: 'center', display: 'inline' },
  thumbnailGallery: {
    display: 'grid',
    gridGap: '16px',
    padding: '16px',
    gridTemplateColumns: 'repeat(auto-fill,minmax(100px,1fr))',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(100px,1fr)',
    overflowX: 'auto'
  },
  zoomableGalleryDiv: {
    display: 'grid',
    gridTemplateColumns: '40px auto 40px'
  },
  mainZoomableGalleryDiv: {
    maxWidth: '620px',
    maxHeight: '620px'
  },
  mainZoomableGalleryImage: {
    border: 'solid #A7B6C2 2px',
    height: '100%',
    maxHeight: '620px',
    maxWidth: '620px'
  }
}

export const FailableImage = ({ src, ...props }) => {
  const [failed, setfailed] = useState(false)

  return (
    <>
      {failed || src === undefined ? (
        <div className="image-404">
          <Icon color={Colors.RED3} iconSize={24} icon="warning-sign" />
          <p className="bp3-text-small bp3-text-muted">This image can not be found, please remove and try again.</p>
        </div>
      ) : (
        <img
          src={src}
          {...props}
          alt=""
          onError={() => {
            setfailed(true)
          }}
        />
      )}
    </>
  )
}

const ThumbnailGalleryItem = ({ url, index, currentImageIndex, setCurrentImageIndex, rotation }) => {
  const ref = useRef<HTMLDivElement>()
  useEffect(() => {
    const { current } = ref
    if (index === currentImageIndex && current) {
      current?.scrollIntoView()
    }
  }, [index, currentImageIndex])

  const hackRotation = ImageHelpers.hackRotationFn(url)

  return (
    <div onClick={() => setCurrentImageIndex(index)} ref={ref} style={style.thumbnailGalleryItem}>
      <FailableImage
        src={url}
        className="img-selection-img"
        alt={`number ${index + 1}`}
        style={{
          border: `2px solid ${currentImageIndex === index ? ' #337dff' : ' #A7B6C2'}`,
          transform: `translatez(0) rotate(${-Math.abs(hackRotation - rotation || 0)}deg)`
        }}
      />
    </div>
  )
}

const ThumbnailGallery = ({ items, currentImageIndex, setCurrentImageIndex }) => {
  const thumbnailItems = items.map((item, index) => {
    return (
      <ThumbnailGalleryItem
        url={item.url}
        index={index}
        currentImageIndex={currentImageIndex}
        setCurrentImageIndex={setCurrentImageIndex}
        rotation={item.rotation}
      />
    )
  })
  return <div style={style.thumbnailGallery}>{thumbnailItems}</div>
}

export const ZoomableGallery = ({ currentImageIndex, setCurrentImageIndex, items }) => {
  const [src, setSrc] = useState('')
  const [rotation, setRotation] = useState(0)

  useEffect(() => {
    const currentImage = items[currentImageIndex]

    if (currentImage !== undefined) {
      setSrc(currentImage.url || currentImage.preview)
      setRotation(currentImage.rotation)
    }
  }, [currentImageIndex, items])

  const handleDelta = (value, delta, max, min) => {
    const newValue = value + delta
    const inRange = newValue > min && newValue < max
    return inRange ? newValue : value
  }

  useEffect(() => {
    const keyDown = e => {
      switch (e.key) {
        case 'ArrowLeft':
          setCurrentImageIndex(v => handleDelta(v, -1, items.length, -1))
          break
        case 'ArrowRight':
          setCurrentImageIndex(v => handleDelta(v, 1, items.length, -1))
          break
        default:
          break
      }
    }

    document.addEventListener('keydown', keyDown)
    return () => {
      document.removeEventListener('keydown', keyDown)
    }
  }, [currentImageIndex, setCurrentImageIndex, items])

  const onClose = () => {
    setCurrentImageIndex()
  }

  const hackRotation = ImageHelpers.hackRotationFn(src)

  return (
    <Dialog
      style={style.zoomDialog}
      canEscapeKeyClose
      canOutsideClickClose
      onClose={onClose}
      isOpen={currentImageIndex !== undefined}
    >
      <div style={style.zoomableGalleryDiv}>
        <Icon
          icon={IconNames.CARET_LEFT}
          iconSize={Icon.SIZE_LARGE}
          onClick={() => setCurrentImageIndex(v => handleDelta(v, -1, items.length, -1))}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: currentImageIndex === 0 ? '#A7B6C2' : undefined
          }}
        />
        <Icon icon={IconNames.CROSS} onClick={onClose} className="base-image-icon" style={style.zoomExitBtn} />
        <div style={{ paddingTop: '10px' }}>
          <div
            style={{
              height: '620px',
              width: '620px',
              transform: `translatez(0) rotate(${-Math.abs(hackRotation - rotation || 0)}deg)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div style={style.mainZoomableGalleryDiv}>
              <FailableImage src={src} alt={`number ${currentImageIndex + 1}`} style={style.mainZoomableGalleryImage} />
            </div>
          </div>
        </div>
        <Icon
          icon={IconNames.CARET_RIGHT}
          iconSize={Icon.SIZE_LARGE}
          onClick={() => setCurrentImageIndex(v => handleDelta(v, 1, items.length, -1))}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: currentImageIndex === items.length - 1 ? '#A7B6C2' : undefined
          }}
        />
      </div>
      <ThumbnailGallery
        items={items}
        currentImageIndex={currentImageIndex}
        setCurrentImageIndex={setCurrentImageIndex}
      />
    </Dialog>
  )
}

type SortableGalleryItemProps = {
  id: string
  f: any
  ii: number
  useDragHandle: boolean
  isDisabled: boolean
  disableEdit?: boolean
  removeImage: SortableImageListProps['removeImage']
  rotateImage: SortableImageListProps['rotateImage']
  revertImageEdit: SortableImageListProps['revertImageEdit']
  editImage: SortableImageListProps['editImage']
  setCurrentImageIndex: (index: number) => void
}

const SortableGalleryItem = ({
  id,
  f,
  ii,
  useDragHandle,
  isDisabled,
  disableEdit,
  removeImage,
  rotateImage,
  revertImageEdit,
  editImage,
  setCurrentImageIndex
}: SortableGalleryItemProps) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isDisabled
  })

  const { url, preview, loading, failed, progress, rotation, isEdited: itemIsEdited } = f
  const value = url || preview
  const isEdited = !!itemIsEdited

  const sortableStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.88 : 1,
    zIndex: isDragging ? 2 : undefined,
    touchAction: 'none'
  }

  const cn = CX('img-selection bp3-elevation-1')

  if (failed) {
    // Failed tiles are still reorderable; use the whole card as the drag target (no separate handle).
    return (
      <div
        ref={setNodeRef}
        style={{ ...style.imgContainer, ...sortableStyle }}
        className={cn}
        {...listeners}
        {...attributes}
      >
        <p className="bp3-text-muted">Image Upload Failed</p>
        <Button
          small
          title="Remove image"
          onMouseDown={e => e.stopPropagation()}
          onClick={() => removeImage(ii)}
          className="remove-image-icon"
          icon={IconNames.DELETE}
        />
      </div>
    )
  }

  const hackRotation = ImageHelpers.hackRotationFn(value)

  const outerProps =
    useDragHandle || isDisabled
      ? {}
      : {
          ...listeners,
          ...attributes
        }

  return (
    <div ref={setNodeRef} style={{ ...style.imgContainer, ...sortableStyle }} className={cn} {...outerProps}>
      {loading ? (
        <Spinner value={progress} />
      ) : (
        <FailableImage
          src={value}
          className="img-selection-img"
          alt={`number ${ii + 1}`}
          style={{ transform: `translatez(0) rotate(${-Math.abs(hackRotation - rotation || 0)}deg)` }}
        />
      )}
      {ii === 0 && !loading && (
        <Tag className="main-img" intent={Intent.PRIMARY}>
          Main Image
        </Tag>
      )}
      {!loading && !isDisabled && useDragHandle && (
        <div
          ref={setActivatorNodeRef}
          className="drag-handle"
          {...listeners}
          {...attributes}
          onClick={() => setCurrentImageIndex(ii)}
        />
      )}
      {!loading && !isDisabled && (
        <Button
          small
          title="Remove image"
          onClick={() => removeImage(ii)}
          className="remove-image-icon"
          icon={IconNames.DELETE}
        />
      )}
      {!loading && !isDisabled && (
        <Button
          disabled={isEdited}
          small
          title={
            isEdited
              ? "You can't use this button if you've used the image editor. Click the pencil to rotate instead."
              : 'Rotate image'
          }
          onClick={() => rotateImage(ii)}
          className="rotate-image-icon"
          icon={IconNames.REFRESH}
        />
      )}
      {!loading && !isDisabled && (
        <Button
          onClick={() => setCurrentImageIndex(ii)}
          tabIndex={0}
          style={style.zoomBtn}
          small
          icon={IconNames.ZOOM_IN}
          className="base-image-icon"
        />
      )}

      {config.IMAGE_EDITOR() && !loading && isEdited && (
        <Button
          title="Undo image edits"
          small
          icon="undo"
          className="revert-edit-link"
          intent={Intent.NONE}
          onClick={() => revertImageEdit(ii)}
        />
      )}

      {config.IMAGE_EDITOR() && !loading && !isDisabled && (
        <Button
          small
          icon="edit"
          title="Edit image"
          className="edit-link"
          intent={Intent.NONE}
          onClick={() => editImage(ii)}
          disabled={disableEdit}
        />
      )}
    </div>
  )
}

function sortStrategyForAxis(axis: SortableImageListProps['axis']) {
  if (axis === 'x') return horizontalListSortingStrategy
  if (axis === 'y') return verticalListSortingStrategy
  return rectSortingStrategy
}

const SortableList = ({
  removeImage,
  rotateImage,
  revertImageEdit,
  editImage,
  items,
  axis = 'xy',
  useDragHandle = true,
  distance = 10,
  isDisabled = false,
  disableEdit,
  children,
  onSortEnd
}: SortableImageListProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState<number | undefined>()

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: distance ?? 10 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const itemIds = useMemo(() => items.map((it, i) => getSortableImageId(it, i)), [items])

  const handleDragEnd = (event: DragEndEvent) => {
    if (!onSortEnd) return
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = itemIds.indexOf(String(active.id))
    const newIndex = itemIds.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    onSortEnd({ oldIndex, newIndex })
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
      <div id="image-gallery-container" className="image-gallery-container">
        <ZoomableGallery
          currentImageIndex={currentImageIndex}
          setCurrentImageIndex={setCurrentImageIndex}
          items={items}
        />
        {children}
        <SortableContext items={itemIds} strategy={sortStrategyForAxis(axis)}>
          {items.map((f, i) => (
            <SortableGalleryItem
              key={itemIds[i]}
              id={itemIds[i]}
              f={f}
              ii={i}
              useDragHandle={useDragHandle}
              isDisabled={isDisabled}
              disableEdit={disableEdit}
              removeImage={removeImage}
              rotateImage={rotateImage}
              revertImageEdit={revertImageEdit}
              editImage={editImage}
              setCurrentImageIndex={setCurrentImageIndex}
            />
          ))}
        </SortableContext>
      </div>
    </DndContext>
  )
}

export default SortableList
